"use strict";
/**
 * Czech spell checking service
 * Provides spell checking functionality for Czech text using nspell and Hunspell dictionary
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.spellCheckService = void 0;
const nspell_1 = __importDefault(require("nspell"));
class SpellCheckService {
    constructor() {
        this.spellChecker = null;
        this.initialized = false;
        this.initializing = false; // Prevent multiple initializations
        this.wordCache = new Map(); // Cache pro správnost slov
        this.suggestionCache = new Map(); // Cache pro návrhy
    }
    /**
     * Initialize the spell checker with Czech Hunspell dictionary using nspell
     */
    async initialize() {
        if (this.initialized)
            return;
        if (this.initializing) {
            // Wait for existing initialization to complete
            while (this.initializing) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            return;
        }
        this.initializing = true;
        try {
            console.log('Loading Czech dictionary with nspell...');
            // Load dictionary files manually
            const [affResponse, dicResponse] = await Promise.all([
                fetch('/dictionaries/cs_CZ/cs_CZ.aff'),
                fetch('/dictionaries/cs_CZ/cs_CZ.dic')
            ]);
            if (!affResponse.ok || !dicResponse.ok) {
                throw new Error('Failed to load dictionary files');
            }
            const affText = await affResponse.text();
            const dicText = await dicResponse.text();
            // Initialize nspell with the Czech dictionary
            this.spellChecker = (0, nspell_1.default)(affText, dicText);
            this.initialized = true;
            console.log('Czech spell checker initialized with nspell');
        }
        catch (error) {
            console.error('Failed to initialize spell checker:', error);
            this.initialized = false;
        }
        finally {
            this.initializing = false;
        }
    }
    /**
     * Check spelling of a text (optimized with caching)
     */
    async checkSpelling(text) {
        // If spell checker is not available, return no errors
        if (!this.initialized) {
            try {
                await this.initialize();
            }
            catch (error) {
                console.warn('Spell checker not available, skipping spell check');
                return {
                    text,
                    errors: [],
                    isValid: true
                };
            }
        }
        // If still not initialized, return no errors
        if (!this.spellChecker) {
            return {
                text,
                errors: [],
                isValid: true
            };
        }
        const errors = [];
        const words = this.extractWords(text);
        for (const wordInfo of words) {
            const isCorrect = this.isWordCorrect(wordInfo.word);
            if (!isCorrect) {
                errors.push({
                    word: wordInfo.word,
                    isCorrect: false,
                    suggestions: [], // Návrhy načteme lazy při kliknutí
                    position: wordInfo.position
                });
            }
        }
        return {
            text,
            errors,
            isValid: errors.length === 0
        };
    }
    /**
     * Get suggestions for a specific word (lazy loading)
     */
    async getSuggestionsForWord(word) {
        if (this.suggestionCache.has(word)) {
            return this.suggestionCache.get(word);
        }
        const suggestions = this.getSuggestions(word);
        this.suggestionCache.set(word, suggestions);
        return suggestions;
    }
    /**
     * Extract words with their positions from text
     */
    extractWords(text) {
        const words = [];
        // Regex bez word boundaries - hledá sekvence písmen (včetně českých znaků)
        // Oddělené mezerami, interpunkcí nebo začátkem/koncem řetězce
        const wordRegex = /[a-záčďéěíňóřšťúůýžA-ZÁČĎÉĚÍŇÓŘŠŤÚŮÝŽ]+/g;
        let match;
        while ((match = wordRegex.exec(text)) !== null) {
            words.push({
                word: match[0],
                position: {
                    start: match.index,
                    end: match.index + match[0].length
                }
            });
        }
        return words;
    }
    /**
     * Check if a word is correct using nspell (with caching)
     */
    isWordCorrect(word) {
        // Check cache first
        if (this.wordCache.has(word)) {
            return this.wordCache.get(word);
        }
        if (!this.spellChecker) {
            return true; // If not initialized, assume correct
        }
        // Check if it's a number
        if (/^\d+$/.test(word)) {
            this.wordCache.set(word, true);
            return true;
        }
        // Check if it's a URL or email
        if (word.includes('@') || word.includes('.com') || word.includes('.cz')) {
            this.wordCache.set(word, true);
            return true;
        }
        // Use nspell to check spelling
        const isCorrect = this.spellChecker.correct(word);
        this.wordCache.set(word, isCorrect);
        return isCorrect;
    }
    /**
     * Get spelling suggestions for a word using nspell
     */
    getSuggestions(word) {
        if (!this.spellChecker) {
            return [];
        }
        try {
            // Use nspell to get suggestions
            const suggestions = this.spellChecker.suggest(word);
            return suggestions.slice(0, 5); // Return top 5 suggestions
        }
        catch (error) {
            console.error('Error getting suggestions:', error);
            return [];
        }
    }
    /**
     * Add word to personal dictionary
     */
    addToPersonalDictionary(word) {
        if (this.spellChecker) {
            this.spellChecker.add(word);
        }
    }
    /**
     * Remove word from personal dictionary
     */
    removeFromPersonalDictionary(word) {
        if (this.spellChecker) {
            this.spellChecker.remove(word);
        }
    }
}
// Export singleton instance
exports.spellCheckService = new SpellCheckService();
